import React, { useMemo, useState } from 'react';
import { Layers, Loader2, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { BillingRunParams, Customer, InvoiceConfig } from '../../types';
import { Button } from '../Button';
import { previewRun, executeBillingRun } from '../../services/billingRunService';
import {
    BillingCandidate,
    BillingRunPreview,
    summarizeExclusions,
} from '../../services/billingRunLogic';
import { centsFromEuroInput, formatCents } from '../../services/money';
import { ProjectCheckChip } from './InvoiceStatusBadge';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';

/**
 * Assistent fuer den Rechnungslauf.
 *
 * Drei Schritte: auswaehlen, Trockenlauf ansehen, Entwuerfe erzeugen. Der
 * Trockenlauf ist keine Schaetzung — er benutzt dieselbe Funktion wie die
 * Ausfuehrung, der Nutzer sieht also vorher exakt das Ergebnis.
 *
 * Ausgestellt wird hier NICHTS. Der Lauf endet bei Entwuerfen, die in die
 * Pruefung gehen.
 */

type Step = 'select' | 'preview' | 'done';

interface Props {
    invoiceConfig: InvoiceConfig;
    today: string;
    /**
     * Der Kundenstamm des Aufrufers — nur fuer die Namen der Gruppen. Die
     * Projektliste traegt keinen Kundennamen, ohne ihn stuende die Kunden-ID da.
     */
    customers: Customer[];
    onClose: () => void;
    /** Nach dem Lauf: Rechnungsliste neu laden. */
    onCreated: () => void;
}

export const BillingRunWizard: React.FC<Props> = ({
    invoiceConfig,
    today,
    customers,
    onClose,
    onCreated,
}) => {
    const [step, setStep] = useState<Step>('select');
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [error, setError] = useState<string | null>(null);

    const [periodFrom, setPeriodFrom] = useState('');
    const [periodTo, setPeriodTo] = useState('');
    const [minNetInput, setMinNetInput] = useState('');
    const [groupBy, setGroupBy] = useState<'customer' | 'project'>('customer');
    const [includeYellow, setIncludeYellow] = useState(false);
    const [includeUndated, setIncludeUndated] = useState(false);
    // Voreinstellung aus der Konfiguration: der Lauf soll dieselben Belege
    // erzeugen wie die einzelne Rechnung, nicht eine zweite Bauart.
    const [cumulative, setCumulative] = useState(
        invoiceConfig.defaultBillingMode === 'CUMULATIVE',
    );

    const [preview, setPreview] = useState<BillingRunPreview | null>(null);
    /**
     * Welche Gruppen tatsaechlich angelegt werden.
     *
     * Nach der Vorschau sind alle gewaehlt — sie ist ja das Ergebnis der
     * Filter. Wer einzelne herausnehmen will, hakt sie ab; das ist der
     * haeufigere Fall als eine leere Liste, aus der man sich alles
     * zusammensucht.
     */
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [candidates, setCandidates] = useState<BillingCandidate[]>([]);
    const [result, setResult] = useState<{ created: number; failures: number } | null>(null);

    const minNetCents = centsFromEuroInput(minNetInput);
    // Eine unlesbare Eingabe wie "50,-" wuerde ueber `?? undefined` still zu
    // "kein Mindestbetrag" — der Nutzer glaubt, Kleinstbetraege seien gefiltert.
    const minNetUnreadable = minNetInput.trim().length > 0 && minNetCents == null;

    const params = useMemo(
        (): BillingRunParams => ({
            periodFrom: periodFrom || undefined,
            periodTo: periodTo || undefined,
            minNetCents: minNetCents ?? undefined,
            // Die Gruppierung gilt fuer die fortschreibenden Projekte. Die
            // kumulativen nimmt der Trockenlauf ohnehin einzeln heraus: ihren
            // Gesamtstand gibt es nur je Projekt.
            groupBy,
            documentType: 'INVOICE',
            invoiceDate: today,
            includeYellow,
            cumulative,
            includeUndated,
        }),
        [
            periodFrom,
            periodTo,
            minNetCents,
            groupBy,
            includeYellow,
            cumulative,
            includeUndated,
            today,
        ],
    );

    const customerNames = useMemo(
        () => new Map(customers.map((c) => [c.id, c.name] as const)),
        [customers],
    );

    const runPreview = async () => {
        setBusy(true);
        setError(null);
        try {
            const out = await previewRun(
                params,
                invoiceConfig,
                today,
                (done, total) => setProgress({ done, total }),
                customerNames,
            );
            setPreview(out.preview);
            setSelected(new Set(out.preview.groups.map((g) => g.key)));
            setCandidates(out.candidates);
            setStep('preview');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Der Trockenlauf ist fehlgeschlagen.');
        } finally {
            setBusy(false);
        }
    };

    const runExecute = async () => {
        if (!preview) return;
        setBusy(true);
        setError(null);
        try {
            // Nur die abgehakten Gruppen. Der Bericht des Laufs traegt die
            // Vorschau unveraendert, damit spaeter nachvollziehbar bleibt, was
            // zur Auswahl stand — angelegt wird aber nur das Gewaehlte.
            const chosen: BillingRunPreview = {
                ...preview,
                groups: preview.groups.filter((g) => selected.has(g.key)),
            };
            const out = await executeBillingRun(chosen, candidates, params, invoiceConfig, today);
            setResult({ created: out.createdInvoiceIds.length, failures: out.failures.length });
            const notes: string[] = [];
            if (out.failures.length) {
                notes.push(
                    `${out.failures.length} Gruppe(n) konnten nicht angelegt werden: ${out.failures
                        .map(
                            (f) =>
                                `${f.customerName ?? f.customerId} [${f.projectNames.join(', ')}] (${f.message})`,
                        )
                        .join('; ')}`,
                );
            }
            // Angelegt, aber unfertig. Gehoert in dieselbe Meldung und nicht
            // unter die Fehlschlaege: die Entwuerfe stehen in der Liste, ihnen
            // fehlt nur eine Angabe, die kein Lauf erraten kann.
            if (out.notes.length) {
                notes.push(
                    `${out.notes.length} Entwurf/Entwürfe brauchen noch Nacharbeit: ${out.notes
                        .map(
                            (n) =>
                                `${n.customerName ?? n.customerId} [${n.projectNames.join(', ')}] (${n.message})`,
                        )
                        .join('; ')}`,
                );
            }
            // Die Entwuerfe sind angelegt, nur das Laufprotokoll nicht. Das
            // muss anders klingen als ein Fehlschlag — sonst wiederholt der
            // Nutzer den Lauf und verliert die Nachvollziehbarkeit.
            if (out.runLogError) {
                notes.push(
                    `Die Entwürfe wurden angelegt, das Laufprotokoll konnte aber nicht fortgeschrieben werden (${out.runLogError}). Bitte den Lauf NICHT wiederholen.`,
                );
            }
            if (notes.length) setError(notes.join(' — '));
            setStep('done');
            onCreated();
        } catch (e) {
            // Frischen Trockenlauf erzwingen: die alte Vorschau stammt von vor
            // dem Versuch. Ein zweiter Klick wuerde sie unveraendert erneut
            // ausfuehren, obwohl ein Teil der Entwuerfe schon angelegt sein kann
            // und Mengen inzwischen anders beansprucht sind.
            setPreview(null);
            setCandidates([]);
            setStep('select');
            setError(
                `${rechtFehlerOder(e, () => (e instanceof Error ? e.message : 'Der Lauf ist fehlgeschlagen.'))} Bitte den Trockenlauf wiederholen und das Ergebnis prüfen — die vorherige Vorschau ist nicht mehr gültig.`,
            );
        } finally {
            setBusy(false);
        }
    };

    const exclusionSummary = useMemo(
        () => (preview ? summarizeExclusions(preview.excluded) : []),
        [preview],
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-fade-in backdrop-blur-sm p-4">
            <div className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-800">
                        <Layers className="h-5 w-5 text-slate-400" />
                        Rechnungslauf
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {error && (
                    <div className="border-b border-red-200 bg-red-50 px-5 py-2 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {step === 'select' && (
                    <div className="space-y-4 p-5">
                        <p className="text-sm text-slate-600">
                            Wählt Projekte mit offenen Positionen aus und erzeugt je Kunde einen
                            Rechnungsentwurf. Ausgestellt wird nichts — die Entwürfe gehen in die
                            Prüfung.
                        </p>

                        {/* Kumulativ zuerst: aus dieser Wahl folgt, WIE die
                            Belege gebaut werden — alles darunter haengt daran. */}
                        <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                            <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={cumulative}
                                onChange={(e) => setCumulative(e.target.checked)}
                            />
                            <span>
                                <strong>Kumulativ für neue Projekte.</strong> Projekte, die bereits
                                Rechnungen haben, behalten <em>ihr</em> Modell — der Lauf stellt
                                keines um. Diese Wahl gilt nur für Projekte, die noch keinen Beleg
                                haben und sich deshalb an nichts orientieren können.
                                <span className="mt-1 block text-xs text-slate-500">
                                    Kumulativ heißt: der Beleg zeigt den <em>gesamten</em>
                                    {' '}Leistungsstand des Projekts und zieht ab, was frühere
                                    Rechnungen bereits gefordert haben. Solche Projekte bekommen
                                    immer einen eigenen Beleg — den Gesamtstand gibt es nur je
                                    Projekt, nicht über mehrere hinweg.
                                </span>
                            </span>
                        </label>

                        {/* Monatswahl wie bei der einzelnen Rechnung: der
                            Regelfall ist "ein Monat", und den zweimal als
                            Datum einzutippen ist zweimal Gelegenheit, sich zu
                            vertippen. */}
                        <label className="block text-sm">
                            <span className="mb-1 block text-slate-600">Monat</span>
                            <input
                                type="month"
                                value={periodFrom.slice(0, 7)}
                                onChange={(e) => {
                                    const month = e.target.value;
                                    if (!month) {
                                        setPeriodFrom('');
                                        setPeriodTo('');
                                        return;
                                    }
                                    const [y, m] = month.split('-').map(Number);
                                    // Tag 0 des Folgemonats ist der letzte des
                                    // gewaehlten — spart eine Tabelle mit
                                    // Monatslaengen und trifft den Februar im
                                    // Schaltjahr.
                                    const last = new Date(y, m, 0).getDate();
                                    setPeriodFrom(`${month}-01`);
                                    setPeriodTo(`${month}-${String(last).padStart(2, '0')}`);
                                }}
                                className="w-full rounded border border-slate-300 px-2 py-1.5 sm:w-1/2"
                            />
                        </label>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="block text-sm">
                                <span className="mb-1 block text-slate-600">Leistung ab</span>
                                <input
                                    type="date"
                                    value={periodFrom}
                                    onChange={(e) => setPeriodFrom(e.target.value)}
                                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                                />
                            </label>
                            <label className="block text-sm">
                                <span className="mb-1 block text-slate-600">Leistung bis</span>
                                <input
                                    type="date"
                                    value={periodTo}
                                    onChange={(e) => setPeriodTo(e.target.value)}
                                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                                />
                            </label>
                        </div>
                        <p className="-mt-2 text-xs text-slate-500">
                            Ohne Zeitraum werden alle offenen Positionen abgerechnet. Mit Zeitraum
                            wird höchstens die Menge abgerechnet, die darin auch geleistet wurde.
                        </p>

                        {/* Nur mit Zeitraum: ohne ihn ist ohnehin alles dabei,
                            und der Haken waere eine Frage ohne Wirkung. */}
                        {(periodFrom || periodTo) && (
                            <label className="-mt-2 flex items-start gap-2 text-xs text-slate-600">
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    checked={includeUndated}
                                    onChange={(e) => setIncludeUndated(e.target.checked)}
                                />
                                <span>
                                    <strong>Mengen ohne Datum mitnehmen.</strong> Wer den
                                    Leistungsstand von Hand korrigiert, erzeugt keinen Eintrag im
                                    Leistungsprotokoll — solche Mengen gehören zu keinem Tag und
                                    fallen durch jeden Zeitraum. Ohne diesen Haken bleiben sie im
                                    Lauf dauerhaft liegen, und das Projekt erscheint als „nichts
                                    offen". Mit ihm schreibst du sie diesem Zeitraum zu.
                                </span>
                            </label>
                        )}

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="block text-sm">
                                <span className="mb-1 block text-slate-600">
                                    Bagatellgrenze netto €
                                </span>
                                <input
                                    inputMode="decimal"
                                    value={minNetInput}
                                    onChange={(e) => setMinNetInput(e.target.value)}
                                    placeholder="z. B. 50,00"
                                    className={`w-full rounded border px-2 py-1.5 text-right tabular-nums ${
                                        minNetUnreadable
                                            ? 'border-red-400 bg-red-50'
                                            : 'border-slate-300'
                                    }`}
                                />
                                {minNetUnreadable && (
                                    <span className="mt-1 block text-xs text-red-600">
                                        Die Bagatellgrenze ist nicht lesbar. Beispiel: 50,00
                                    </span>
                                )}
                            </label>
                            <label className="block text-sm">
                                <span className="mb-1 block text-slate-600">Gruppierung</span>
                                <select
                                    value={groupBy}
                                    onChange={(e) =>
                                        setGroupBy(e.target.value as 'customer' | 'project')
                                    }
                                    className="w-full rounded border border-slate-300 px-2 py-1.5"
                                >
                                    <option value="customer">
                                        Je Kunde (Sammelrechnung über Projekte)
                                    </option>
                                    <option value="project">Je Projekt (eigene Rechnung)</option>
                                </select>
                                <span className="mt-1 block text-xs text-slate-500">
                                    Kumulative Projekte bekommen unabhängig davon einen eigenen
                                    Beleg.
                                </span>
                            </label>
                        </div>

                        <label className="flex items-start gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={includeYellow}
                                onChange={(e) => setIncludeYellow(e.target.checked)}
                                className="mt-0.5"
                            />
                            <span>
                                Gelbe Projekte aufnehmen
                                <span className="block text-xs text-slate-500">
                                    Auffällige Projekte kommen in die Prüfung und brauchen vor dem
                                    Ausstellen eine dokumentierte Kenntnisnahme. Rote Projekte sind
                                    nie dabei.
                                </span>
                            </span>
                        </label>

                        <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
                            <Button variant="secondary" onClick={onClose}>
                                Abbrechen
                            </Button>
                            {/* Gesperrt statt stillschweigend ohne Grenze zu laufen. */}
                            <Button
                                onClick={() => void runPreview()}
                                disabled={busy || minNetUnreadable}
                            >
                                {busy ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {progress.done} von {progress.total} geprüft
                                    </>
                                ) : (
                                    'Trockenlauf'
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {step === 'preview' && preview && (
                    <>
                        <div className="border-b border-slate-200 px-5 py-3">
                            <div className="flex flex-wrap items-center gap-4 text-sm">
                                <span>
                                    <span className="font-semibold">{preview.totals.groups}</span>{' '}
                                    {preview.totals.groups === 1 ? 'Entwurf' : 'Entwürfe'}
                                </span>
                                <span className="text-slate-500">
                                    aus {preview.totals.projects}{' '}
                                    {preview.totals.projects === 1 ? 'Projekt' : 'Projekten'}
                                </span>
                                <span>
                                    netto{' '}
                                    <span className="font-semibold tabular-nums">
                                        {formatCents(preview.totals.netCents)}
                                    </span>
                                </span>
                                <span>
                                    brutto{' '}
                                    <span className="font-semibold tabular-nums">
                                        {formatCents(preview.totals.grossCents)}
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto">
                            {preview.groups.length === 0 ? (
                                <div className="px-5 py-6 text-sm text-slate-600">
                                    <p className="font-medium">Nichts abzurechnen.</p>
                                    <p className="mt-1">
                                        Entweder sind alle Mengen bereits abgerechnet, oder ein
                                        anderer Entwurf beansprucht sie. Das ist zugleich der
                                        Beweis der Wiederholbarkeit: ein zweiter Lauf über dieselbe
                                        Auswahl erzeugt nichts.
                                    </p>
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
                                        <tr>
                                            <th className="px-3 py-2">
                                                <input
                                                    type="checkbox"
                                                    title="Alle aus- oder abwählen"
                                                    checked={
                                                        preview.groups.length > 0 &&
                                                        preview.groups.every((g) =>
                                                            selected.has(g.key),
                                                        )
                                                    }
                                                    onChange={(e) =>
                                                        setSelected(
                                                            e.target.checked
                                                                ? new Set(
                                                                      preview.groups.map(
                                                                          (g) => g.key,
                                                                      ),
                                                                  )
                                                                : new Set(),
                                                        )
                                                    }
                                                />
                                            </th>
                                            <th className="px-3 py-2">Ampel</th>
                                            <th className="px-3 py-2">Kunde</th>
                                            <th className="px-3 py-2">Projekte</th>
                                            <th className="px-3 py-2">Modell</th>
                                            <th className="px-3 py-2 text-right">Netto</th>
                                            <th className="px-3 py-2 text-right">Brutto</th>
                                            <th className="px-3 py-2 text-right">Befunde</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {preview.groups.map((g) => (
                                            <tr
                                                key={g.key}
                                                className={
                                                    selected.has(g.key) ? '' : 'opacity-40'
                                                }
                                            >
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(g.key)}
                                                        onChange={(e) => {
                                                            const next = new Set(selected);
                                                            if (e.target.checked) next.add(g.key);
                                                            else next.delete(g.key);
                                                            setSelected(next);
                                                        }}
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <ProjectCheckChip traffic={g.traffic} />
                                                </td>
                                                <td className="px-3 py-2 font-medium text-slate-800">
                                                    {g.customerName ?? g.customerId}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-slate-600">
                                                    {g.projects
                                                        .map((p) => p.projectNumber ?? p.projectName)
                                                        .join(', ')}
                                                </td>
                                                {/* Das Modell folgt dem Projekt und
                                                    wird hier nur berichtet — es ist
                                                    keine Wahl des Laufs. */}
                                                <td className="px-3 py-2">
                                                    <span
                                                        className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                                                            g.cumulative
                                                                ? 'bg-emerald-50 text-emerald-700'
                                                                : 'bg-slate-100 text-slate-500'
                                                        }`}
                                                    >
                                                        {g.cumulative
                                                            ? 'kumulativ'
                                                            : 'fortschreibend'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {formatCents(g.netCents)}
                                                </td>
                                                <td className="px-3 py-2 text-right font-medium tabular-nums">
                                                    {formatCents(g.grossCents)}
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    {g.findingCount || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {exclusionSummary.length > 0 && (
                                <div className="border-t border-slate-200 px-5 py-3">
                                    <p className="mb-1 flex items-center gap-1.5 text-sm font-medium text-slate-700">
                                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                                        Nicht aufgenommen ({preview.excluded.length})
                                    </p>
                                    {/* Nicht nur "3× gesperrt": mit einer blossen
                                        Zahl weiss niemand, WELCHES Projekt fehlt
                                        und was daran zu tun ist. Der Befund steht
                                        im detail — er gehoert hierher. */}
                                    <ul className="space-y-2 text-xs text-slate-600">
                                        {exclusionSummary.map((s) => {
                                            const rows = preview.excluded.filter(
                                                (e) => e.reason === s.reason,
                                            );
                                            return (
                                                <li key={s.reason}>
                                                    <span className="font-medium">{s.count}×</span>{' '}
                                                    {s.text}
                                                    <ul className="mt-0.5 space-y-0.5 border-l-2 border-slate-100 pl-3">
                                                        {rows.slice(0, 8).map((e) => {
                                                            // detail traegt den
                                                            // Gruppentext bereits
                                                            // vorn — hier stuende
                                                            // er sonst zweimal.
                                                            const extra = e.detail
                                                                ?.replace(s.text, '')
                                                                .trim();
                                                            return (
                                                                <li key={e.projectId}>
                                                                    <span className="font-medium text-slate-700">
                                                                        {e.projectName}
                                                                    </span>
                                                                    {extra && (
                                                                        <span className="text-slate-500">
                                                                            {' — '}
                                                                            {extra}
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            );
                                                        })}
                                                        {rows.length > 8 && (
                                                            <li className="text-slate-400">
                                                                … und {rows.length - 8} weitere
                                                            </li>
                                                        )}
                                                    </ul>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
                            <Button variant="secondary" onClick={() => setStep('select')}>
                                Zurück
                            </Button>
                            <Button
                                onClick={() => void runExecute()}
                                disabled={busy || preview.groups.length === 0}
                            >
                                {busy ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Entwürfe werden angelegt …
                                    </>
                                ) : (
                                    `${preview.groups.length} Entwürfe anlegen`
                                )}
                            </Button>
                        </div>
                    </>
                )}

                {step === 'done' && result && (
                    <div className="space-y-3 p-5">
                        <p className="flex items-center gap-2 text-sm text-emerald-700">
                            <CheckCircle2 className="h-5 w-5" />
                            {result.created} {result.created === 1 ? 'Entwurf' : 'Entwürfe'} angelegt.
                        </p>
                        <p className="text-sm text-slate-600">
                            Die Entwürfe stehen jetzt in der Rechnungsliste. Ausgestellt ist noch
                            nichts — jeder Beleg braucht Freigabe und Ausstellung, und beim
                            Ausstellen läuft der Projektcheck noch einmal frisch.
                        </p>
                        <div className="flex justify-end">
                            <Button onClick={onClose}>Schließen</Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
